import Panel from "@/components/ui/Panel";
import Card from "@/components/ui/Card";
import { ButtonAnchor } from "@/components/ui/Button";
import GuideBody from "@/components/guides/GuideBody";
import SponsorPanel from "./SponsorPanel";
import type {
  EventPageBlock,
  HeroBlock,
  TextBlock,
  ImageBlock,
  GalleryBlock,
  ScheduleBlock,
  FaqBlock,
  LinksBlock,
} from "@/interfaces/event_page";

// One organizer-authored section of a public event page. Everything rendered
// here has already been through Event::Page server-side: markdown is sanitized
// HTML by the time it arrives, and every URL has had its scheme checked.

// Boxed like every other section: unboxed headlines float on the page ground,
// and two in a row read as one run-on paragraph rather than two headlines.
function Hero({ block }: { block: HeroBlock }) {
  return (
    <Panel>
      <div className="flex flex-col gap-2">
        {block.heading && (
          <h2 className="font-display text-title text-ink leading-tight font-bold">
            {block.heading}
          </h2>
        )}
        {block.subheading && (
          <p className="text-subheading text-ink-muted">{block.subheading}</p>
        )}
      </div>
    </Panel>
  );
}

function Text({ block }: { block: TextBlock }) {
  // `body` is the markdown source the editor writes back; `html` is what
  // MarkdownRenderer made of it. Only ever render the latter.
  if (!block.html) return null;

  return (
    <Panel>
      <GuideBody html={block.html} />
    </Panel>
  );
}

function Image({ block }: { block: ImageBlock }) {
  return (
    <Card
      media={
        <img
          src={block.url}
          alt={block.caption ?? ""}
          className="block w-full"
          loading="lazy"
        />
      }
      bodyClassName={block.caption ? undefined : "hidden"}
    >
      {block.caption && (
        <p className="text-caption text-ink-soft">{block.caption}</p>
      )}
    </Card>
  );
}

function Gallery({ block }: { block: GalleryBlock }) {
  return (
    <Panel title={block.heading ?? "photos"}>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {block.items.map((item, i) => (
          <figure key={i} className="flex flex-col gap-1">
            <img
              src={item.url}
              alt={item.caption ?? ""}
              loading="lazy"
              className="border-line h-40 w-full border-2 object-cover"
            />
            {item.caption && (
              <figcaption className="text-caption text-ink-soft">
                {item.caption}
              </figcaption>
            )}
          </figure>
        ))}
      </div>
    </Panel>
  );
}

function Schedule({ block }: { block: ScheduleBlock }) {
  return (
    <Panel title={block.heading ?? "schedule.txt"}>
      <ol className="flex flex-col gap-3">
        {block.items.map((item, i) => (
          <li key={i} className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
            {item.time && (
              <span className="font-display text-body text-ink w-24 shrink-0 font-bold">
                {item.time}
              </span>
            )}
            <span className="text-body text-ink-muted">{item.label}</span>
          </li>
        ))}
      </ol>
    </Panel>
  );
}

function Faq({ block }: { block: FaqBlock }) {
  return (
    <Panel title={block.heading ?? "faq.csv"}>
      <dl className="flex flex-col gap-4">
        {block.items.map((item, i) => (
          <div key={i} className="flex flex-col gap-1">
            <dt className="font-display text-subheading text-ink font-bold">
              {item.question}
            </dt>
            <dd>{item.answer_html && <GuideBody html={item.answer_html} />}</dd>
          </div>
        ))}
      </dl>
    </Panel>
  );
}

function Links({ block }: { block: LinksBlock }) {
  return (
    <div className="flex flex-wrap gap-3">
      {block.items.map((item, i) => (
        <ButtonAnchor
          key={i}
          href={item.url}
          variant="secondary"
          target="_blank"
          rel="noopener noreferrer"
        >
          {item.label}
        </ButtonAnchor>
      ))}
    </div>
  );
}

export default function EventPageBlockView({
  block,
}: {
  block: EventPageBlock;
}) {
  switch (block.type) {
    case "hero":
      return <Hero block={block} />;
    case "text":
      return <Text block={block} />;
    case "image":
      return <Image block={block} />;
    case "gallery":
      return <Gallery block={block} />;
    case "schedule":
      return <Schedule block={block} />;
    case "faq":
      return <Faq block={block} />;
    case "links":
      return <Links block={block} />;
    case "sponsors":
      return <SponsorPanel block={block} />;
  }
}
